

















































































import Debug from 'debug'
import { Component, Vue, Prop, Watch, Emit } from 'vue-property-decorator';
import { shuffle } from 'lodash';
import { nextChar } from '@/app/shared/utils/increment-letter';
import draggable from 'vuedraggable';
import { DragStartParams, DropResult } from 'smooth-dnd';
import { SerializedMatching } from '@xlearn/common';
const vuednd = require('vue-smooth-dnd');
import * as Api from "@/app/question/components/api";
import { MatchingAnswer, MatchingAnswerItem, MatchingState } from "./matching-interfaces";
import { Dictionary } from 'vue-router/types/router';
import { TranslateResult } from 'vue-i18n';
import i18n from '@/plugins/i18n';

enum ItemType {
  definition = 'definition',
  concept = 'concept',
}

enum LabelProp {
  first = 'first',
  second = 'second',
}

export interface Pair {
  concept: PairItem;
  definition: PairItem;
  connected: boolean;
}

export interface PairItem {
  id: string;
  pairId: string;
  type: ItemType,
  selected: boolean;
  label: string;
  originalId: string;
}

interface OrderInfo {
  axis: ItemType;
  order: string[];
}

interface QuestionScore {
  correct: number;
  incorrect: number;
}

interface QuestionAnswer {
  id: string;
  score: QuestionScore;
  first: string;
  second: string;
}

export interface MatchingCorrectAnswer {
  concepts: PairItem[];
  definitions: PairItem[];
}

// the answer type which is passed to the solution component
export type MatchingSolutionAnswer = Pair[]

const debug = Debug('matching-question-component')

@Component({
  components: {
    draggable,
    Container: vuednd.Container,
    Draggable: vuednd.Draggable
  }
})
export default class MatchingQuestionComponent extends Vue
  implements Api.QuestionComponent<SerializedMatching, MatchingAnswer, MatchingState, MatchingCorrectAnswer, MatchingSolutionAnswer> {

  @Prop({
    required: true,
  })
  public question: Api.Question<SerializedMatching>;

  @Prop({
    required: true,
  })
  public evaluated: boolean;

  @Prop({
    required: true,
  })
  public answer: Api.Answer<MatchingAnswer> | null;

  @Prop({
    required: true,
  })
  public showSolution: boolean;

  @Prop({
    required: true,
  })
  public state: Api.State<MatchingState> | null;

  private pairs: Pair[] = [];

  private concepts: PairItem[] = [];

  private definitions: PairItem[] = [];

  private dragging: boolean = false;
  private draggedItem: PairItem | null = null;
  private targetItem: PairItem | null = null;

  public solutionProps() {
    const concepts = this.concepts.filter(c => {
      return this.pairs.filter(p => p.concept.id === c.id && !p.connected).length;
    });

    return {
      userAnswer: this.pairs.filter(pair => pair.connected),
      correctAnswer: {
        concepts,
        definitions: this.definitions,
      },
    };
  };

  private mounted(): void {
    debug('mounted', this.state, this.answer);

    this.setDefaultState();
  }

  @Watch('answer')
  private answerChanged(newValue: Api.Answer<MatchingAnswer> | null): void {
    if (newValue === null) {
      debug('null answer arrived, resetting');
      this.setDefaultState();
    }
  }

  @Emit('answer-changed')
  public emitAnswerChanged(): Api.AnswerChangedPayload<MatchingAnswer> {
    let score: number = 0;
    this.question.content.pairs.forEach((a) => {
      const matchedCorrectly: boolean = this.matchedCorrectly(a.id);
      if (matchedCorrectly) {
        score += a.score.correct;
      } else {
        score += a.score.incorrect;
      }
    });

    let pairs: MatchingAnswerItem[] = this.pairs.map(pair => ({
      conceptId: pair.concept.originalId,
      definitionId: pair.definition.originalId,
      connected: pair.connected || false,
    }));

    return {
      answer: {
        pairs,
        pairsOrder: this.pairs.map(pair => pair.concept.originalId)
      },
      score,
    };
  }

  @Emit('state-changed')
  public emitStateChanged(state: Api.StateChangedPayload<MatchingState>): Api.StateChangedPayload<MatchingState> {
    return state;
  }

  private matchedCorrectly(id: string): boolean {
    const pair = this.pairs.find((p) => p.concept.pairId === id);
    if (!pair || !pair.connected || pair.concept.pairId !== pair.definition.pairId) {
      return false;
    }

    return true;
  }

  private setDefaultState(): void {
    const answersOrder: Dictionary<boolean> = {};

    this.answer?.answer.pairs.forEach(ap => {
      answersOrder[ap.conceptId] = ap.connected;
    })

    this.concepts = this.initialConcepts();
    this.definitions = this.initialDefinitions();
    this.pairs = this.createPairs();

    if (this.answer) {
      const pairsOrder = this.answer.answer.pairsOrder;
      this.pairs = this.pairs.sort((a: Pair, b: Pair) =>
        pairsOrder.indexOf(a.concept.originalId) - pairsOrder.indexOf(b.concept.originalId)
      ).map(pair => ({
        ...pair,
        connected: answersOrder[pair.concept.originalId]
      }));
    }

    if (!this.state) {
      this.emitStateChanged({
        state: {
          conceptIds: this.concepts.map(concept => concept.originalId),
          definitionIds: this.definitions.map(definition => definition.originalId),
        }
      });
    }
  }

  private initialConcepts(): PairItem[] {
    return this.mapItems(ItemType.concept, LabelProp.first, '1', this.state?.state.conceptIds);
  }

  private initialDefinitions(): PairItem[] {
    return this.mapItems(ItemType.definition, LabelProp.second, 'A', this.state?.state.definitionIds);
  }

  private mapItems(type: ItemType, labelProp: LabelProp, index: string, pairOrderFromState?: string[]): PairItem[] {
    let items: PairItem[] = [];

    let pairs = shuffle(this.question.content.pairs);

    if (pairOrderFromState) {
      pairs.sort((a, b) =>
        pairOrderFromState.indexOf(a.id) < pairOrderFromState.indexOf(b.id)
        ? -1 : 1
      );
    }

    pairs.forEach((answer: QuestionAnswer) => {
      items.push({
        id: index,
        pairId: answer.id,
        type,
        selected: false,
        label: answer[labelProp],
        originalId: answer.id
      });

      if (typeof index == 'number') {
        (index as number)++;
      } else {
        index = nextChar(index as string) as string;
      }
    });

    return items;
  }

  private onEnter(targetItem: PairItem) {
    if (this.dragging) {
      this.targetItem = targetItem;
    }
  }

  private onLeave(targetItem: PairItem) {
    this.targetItem = null;
  }

  private onDragEnter(item: PairItem) {
    this.targetItem = item;
  }

  private onDragStart(e: DragStartParams, item: PairItem, pair: Pair) {
    if (e.isSource) {
      this.draggedItem = item;
      this.dragging = true;

      $(document).one('mouseup.matching-question', () => {
        this.onDrop(null, pair);
      });
    }
  }

  private onKBDnD(item: PairItem) {
    if (this.evaluated) return;

    this.draggedItem = this.draggedItem ? null : item;
  }

  private onClickHandler(item: PairItem, pair: Pair) {
    if (this.draggedItem && this.draggedItem.id === item.id) {
      this.draggedItem = null;
    } else if (!this.draggedItem || this.draggedItem && this.draggedItem.type === item.type) {
      this.draggedItem = item;
    } else {
      this.targetItem = item;
      this.onDrop(null, pair, true);
    }
  }

  private preventIfDnD(e: KeyboardEvent) {
    if (this.draggedItem) {
      e.preventDefault();
    }
  }

  private onKBMoveUp(e: KeyboardEvent, item: PairItem, index: number) {
    if (this.evaluated) return;

    const scrollableContent = document.querySelector('.scrollable-box');
    const tempScrollTop = scrollableContent ? scrollableContent.scrollTop : null;

    if (this.draggedItem) {
      e.preventDefault();

      if (index > 0) {
        const prevIndex = index - 1;

        this.pairs = this.pairs.map((p, i) => ({
          ...p,
          [item.type]: i === index
            ? this.pairs[prevIndex][item.type]
            : i === prevIndex
            ? this.pairs[index][item.type]
            : p[item.type],
          connected: i === index || i === prevIndex
            ? false
            : p.connected
        }));

        this.$nextTick(() => {
          const el = this.$refs[item.type + this.pairs[prevIndex][item.type].id] as HTMLElement[];

          if (Array.isArray(el) && el.length) {
            el[0].focus();
          }

          if (scrollableContent && tempScrollTop !== null) {
            scrollableContent.scrollTop = tempScrollTop;
          }
        });
      }
    }
  }

  private onKBMoveDown(e: KeyboardEvent, item: PairItem, index: number) {
    if (this.evaluated) return;

    const scrollableContent = document.querySelector('.scrollable-box');
    const tempScrollTop = scrollableContent ? scrollableContent.scrollTop : null;

    if (this.draggedItem) {
      e.preventDefault();

      if (index < this.pairs.length - 1) {
        const nextIndex = index + 1;

        this.pairs = this.pairs.map((p, i) => ({
          ...p,
          [item.type]: i === index
            ? this.pairs[nextIndex][item.type]
            : i === nextIndex
            ? this.pairs[index][item.type]
            : p[item.type],
          connected: i === index || i === nextIndex
            ? false
            : p.connected
        }));

        this.$nextTick(() => {
          const el = this.$refs[item.type + this.pairs[nextIndex][item.type].id] as HTMLElement[];

          if (Array.isArray(el) && el.length) {
            el[0].focus();
          }

          if (scrollableContent && tempScrollTop !== null) {
            scrollableContent.scrollTop = tempScrollTop;
          }
        });
      }
    }
  }

  private onKBToggleAttach(e: KeyboardEvent, pair: Pair) {
    if (this.evaluated) return;

    if (this.draggedItem) {
      e.preventDefault();

      pair.connected = !pair.connected;
    }
  }

  private getDefinitionPayload(index: number) {
    return () => this.definitions[index];
  }

  private getConceptPayload(index: number) {
    return () => this.concepts[index];
  }

  private onDrop(e: DropResult | null, pair: Pair, forceDrop: boolean = false): void {
    if (!this.dragging && !forceDrop) {
      return;
    }

    this.dragging = false;

    if (!this.targetItem) {
      this.detach(pair);
    } else if (this.draggedItem && this.draggedItem.type != this.targetItem.type) {
      const con = this.draggedItem.type === 'concept' ? this.draggedItem : this.targetItem;
      const def = this.draggedItem.type === 'definition' ? this.draggedItem : this.targetItem;
      let changeIndexes: number[] = [];

      const orderInfo: OrderInfo = {
        axis: this.draggedItem.type === 'concept' ? ItemType.definition : ItemType.concept,
        order: []
      };

      orderInfo.order = this.pairs.map((p: Pair) => p[orderInfo.axis].id);

      this.pairs.forEach((p, i) => {
        if (p.concept.id === con.id || p.definition.id === def.id) {
          changeIndexes.push(i);
        }
      });

      if (changeIndexes.length > 1) {
        this.pairs = this.pairs.map((p: Pair, i: number) => {
          return {
            ...p,
            connected: p.concept.id === con.id ? true : p.connected,
            definition:
              i === changeIndexes[0]
                ? this.pairs[changeIndexes[1]].definition
                : i === changeIndexes[1]
                ? this.pairs[changeIndexes[0]].definition
                : p.definition
          }
        });
      } else if (changeIndexes.length === 1) {
        this.pairs = this.pairs.map((p, i) => ({
          ...p,
          connected: i === changeIndexes[0]
            ? true
            : p.connected
        }));
      }

      this.pairs = this.pairs.sort((a, b) =>
        orderInfo.order.indexOf(a[orderInfo.axis].id) >
        orderInfo.order.indexOf(b[orderInfo.axis].id)
          ? 1
          : -1
      );
    }

    this.draggedItem = null;
    this.targetItem = null;

    this.emitAnswerChanged();
  }

  private detach(pair: Pair) {
    if (pair) {
      pair.connected = false;
    }
  }

  private toggleAttach(pair: Pair) {
    if (pair) {
      this.draggedItem = pair.concept;
      this.targetItem = pair.definition;
      this.onDrop(null, pair, true);
    }
  }

  private createPairs(): Pair[] {
    let createdPairs: Pair[] = [];
    let concepts: PairItem[] = this.concepts;
    let definitions: PairItem[] = this.definitions;

    if (this.answer?.answer.pairs) {
      const { pairs } = this.answer.answer;

      pairs.forEach(pair => {
        const concept = this.concepts.find(c => c.originalId == pair.conceptId);
        const definition = this.definitions.find(d => d.originalId == pair.definitionId);

        if (concept && definition) {
          createdPairs.push({
            concept,
            definition,
            connected: pair.connected,
          })
        }
      })
    } else {
      createdPairs = createdPairs.concat(concepts.map((c, i) => ({
        concept: c,
        definition: definitions[i],
        connected: false
      })))
    }

    return createdPairs;
  }

  private validatePair(pair: Pair): string {
    if (!this.showSolution) {
      return '';
    }

    const firstId: string = pair.concept.pairId;
    const secondId: string = pair.definition.pairId;
    const correct: boolean = firstId === secondId;
    return correct ? 'correct' : 'incorrect';
  }

  private t(key: string, params: any = {}): TranslateResult {
    return i18n.t(key, params);
  }
}
